import type { CompanyPageState } from "@/stores/company-page-store";
import type { CompanySectionKey } from "@shaarp/common";
import { createContext, useContext } from "react";

/**
 * What a company card needs to know of the run that fills the company: the
 * status of each section, whether the run is over, and whether it ended on
 * the team's spent enrichment budget.
 */
export type CompanySectionsState = Pick<
  CompanyPageState,
  "sections" | "done" | "budgetSpent"
>;

/**
 * No run shows this company — a side panel opened on a row, a card outside
 * the page: nothing is pending, so a card shows the data it has, or nothing.
 */
export const NO_COMPANY_RUN: CompanySectionsState = {
  sections: {},
  done: true,
  budgetSpent: false,
};

/**
 * The sections of the company on screen, provided by whatever shows it: the
 * desktop tabs, the mobile page, the side panel. Read by `CompanySectionCard`.
 */
export const CompanySectionsContext =
  createContext<CompanySectionsState>(NO_COMPANY_RUN);

/** The sections of the company on screen, or no run outside any provider. */
export function useCompanySections(): CompanySectionsState {
  return useContext(CompanySectionsContext);
}

/** The sections part of a run's state, or no run when there is none. */
export function toCompanySectionsState(
  state: CompanySectionsState | undefined,
): CompanySectionsState {
  if (!state) return NO_COMPANY_RUN;
  return {
    sections: state.sections,
    done: state.done,
    budgetSpent: state.budgetSpent,
  };
}

/**
 * Whether the run's snapshot arrived: it lists every section. Until then a
 * revisit still paints the company an earlier run left, with no section.
 */
export function hasRunSnapshot(state: CompanySectionsState): boolean {
  return Object.keys(state.sections).length > 0;
}

/**
 * Whether one of `keys` is still being enriched. Before a run's snapshot says
 * which sections it enriches, any of them may be: a read gated on its
 * sections waits for it, instead of asking what the run is about to refresh.
 */
export function isAnySectionPending(
  state: CompanySectionsState,
  keys: readonly CompanySectionKey[],
): boolean {
  if (!state.done && !hasRunSnapshot(state)) return keys.length > 0;
  return keys.some((key) => state.sections[key] === "pending");
}

/** Whether one of `keys` is still being enriched in the company on screen. */
export function useAreSectionsPending(
  keys: readonly CompanySectionKey[],
): boolean {
  return isAnySectionPending(useCompanySections(), keys);
}

/**
 * Whether one of `keys` was never run because the team's enrichment budget
 * was spent: such a run sends no section, and the store settles the pending
 * ones `empty` when it ends. Its emptiness says nothing about the company.
 */
export function isAnySectionCutByBudget(
  state: CompanySectionsState,
  keys: readonly CompanySectionKey[],
): boolean {
  return (
    state.budgetSpent && keys.some((key) => state.sections[key] === "empty")
  );
}

/** Whether one of `keys` was cut by the spent budget in the company on screen. */
export function useAreSectionsCutByBudget(
  keys: readonly CompanySectionKey[],
): boolean {
  return isAnySectionCutByBudget(useCompanySections(), keys);
}

/**
 * Whether one of `keys` ended in error: its provider failed, or the run did.
 * Its emptiness says nothing about the company either.
 */
export function isAnySectionFailed(
  state: CompanySectionsState,
  keys: readonly CompanySectionKey[],
): boolean {
  return keys.some((key) => state.sections[key] === "error");
}

/** Whether one of `keys` ended in error in the company on screen. */
export function useAreSectionsFailed(
  keys: readonly CompanySectionKey[],
): boolean {
  return isAnySectionFailed(useCompanySections(), keys);
}
